import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const WhyIBuiltThisPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-white font-sans text-gray-900">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md z-50 border-b border-gray-100">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/" className="text-xl font-bold text-gray-900">SwiftBidPro</Link>
                    <div className="flex items-center gap-4">
                        <Link to="/login" className="text-gray-600 hover:text-gray-900 font-medium">Login</Link>
                        <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-500 transition-colors">
                            Get Started
                        </Link>
                    </div>
                </div>
            </nav>

            <main className="pt-32 pb-24">
                <article className="container mx-auto px-4 max-w-3xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-blue-600 font-bold tracking-wider uppercase text-sm mb-4 block">Our Story</span>
                        <h1 className="text-4xl md:text-6xl font-extrabold mb-8 leading-tight">
                            Why I Built This
                        </h1>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                        className="prose prose-lg prose-blue text-gray-600 leading-relaxed"
                    >
                        <p className="text-xl text-gray-800 font-medium mb-8">
                            I built SwiftBidPro because I was tired of trying to run my small painting business with tools that were never made for people like me.
                        </p>

                        <p>
                            I’ve tried just about every CRM out there. Some were great… but none were built for the one-man army. The owner-operator. The person who answers the phone, drives the truck, does the work, sends the quote, and invoices the job—usually all in the same day.
                        </p>

                        <p>
                            Most software is made for the guy sitting in an office, not the guy covered in paint, dirt, or sawdust.
                        </p>

                        <p>
                            Whenever I looked around at the “tools” available, they were bloated, expensive, and complicated. They were built for companies with dispatchers, office staff, and sales teams—not for someone running a 1–5 person crew who just needs clean data and simple tools to run their business.
                        </p>

                        <h3 className="text-2xl font-bold text-gray-900 mt-12 mb-4">I didn’t need a CRM that integrates with Slack.</h3>
                        <p>
                            I didn’t need to track 17 KPIs I’ll never look at.
                            <br />
                            I needed something that made my day easier.
                            <br />
                            Something that helped me send a quote before I left the driveway.
                            <br />
                            Something that told me, clearly, who owes me money.
                            <br />
                            Something that made the office work the least stressful part of my business.
                        </p>

                        <p className="font-bold text-gray-900 mt-8">
                            So I built it.
                        </p>

                        <p>
                            SwiftBidPro wasn’t made for the 50-employee companies with corporate meetings and quarterly targets.
                            It was made for people like me—the ones choosing between paper-and-pencil notes or crazy expensive enterprise software.
                        </p>

                        <p>
                            No investors. No bloated feature list. No nonsense.
                        </p>

                        <p>
                            Just simple, clean tools built for the working people actually doing the jobs.
                        </p>

                        <div className="my-12 p-8 bg-gray-50 rounded-2xl border-l-4 border-blue-600 italic text-gray-700">
                            "My goal is simple: Make the 'office work' the easiest part of your day, so you can focus on the craft you love."
                        </div>

                        <p>
                            If that sounds like what you’ve been looking for, I’d love to have you.
                        </p>

                        <div className="mt-12 not-prose">
                            <Link to="/register">
                                <button className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-500 transition-all shadow-lg hover:shadow-blue-500/50 transform hover:-translate-y-1">
                                    Join the Founding Members
                                </button>
                            </Link>
                            <p className="mt-4 text-sm text-gray-500">
                                Try it free. No credit card required.
                            </p>
                        </div>
                    </motion.div>
                </article>
            </main>

            <footer className="bg-white border-t border-gray-100 py-12">
                <div className="container mx-auto px-4 text-center text-gray-400">
                    <p>&copy; {new Date().getFullYear()} SwiftBidPro. Built for the trade.</p>
                </div>
            </footer>
        </div>
    );
};

export default WhyIBuiltThisPage;
